// TEMPLATES-T2..T4 company routes (docs/design/2026-10-28-agent-templates.md §1–§3, §6, §7).
// Workforce › Templates browses the role catalog, edits the company's overlay and applies a
// vertical pack; the "Add digital employee" wizard plans a deploy from a role and applies it (the
// access step, KPIs, routines, probation); the DE's profile reads and edits its access and ends
// its probation. Authoring and publishing stay in the agency console (DC-03). Handlers:
// services/api/src/routes/role-templates.ts over domain/role-templates/*.
import { z } from "zod";
import { AgentTemplate } from "../agent-rows";
import { AgentTemplateVersion } from "../de-config-rows";
import { AccessDefaultSource, AccessFamily, AccessLevel, ModelTier } from "../enums";
import { boundedRecord, Timestamp, text, Uuid } from "../primitives";
import {
  AccessCapsJson,
  DeAccessDefault,
  RoleCadenceRow,
  RoleKpiRow,
  RoleProbation,
  RoleRequiredConnection,
  TemplateOverlay,
  TemplateVerticalPack,
} from "../template-rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

const Level4 = z.enum(["R", "A", "D", "M"]);
const SkillKey = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/)
  .max(63);
const ClassKey = z
  .string()
  .regex(/^[a-z][a-z_]{0,63}$/)
  .max(64);
const MetricKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/)
  .max(64);

export const RoleTemplateCard = z
  .object({
    template: AgentTemplate,
    version: AgentTemplateVersion,
    summary: z.string(),
    skills: z.array(z.string()),
    /** Live DEs deployed from this role in the company. */
    in_use: z.int().min(0),
    /** Vertical packs that cover this role (keys). */
    vertical_packs: z.array(z.string()),
  })
  .meta({ id: "RoleTemplateCard" });

export const RoleTemplateList = z
  .object({ data: z.array(RoleTemplateCard), vertical_packs: z.array(TemplateVerticalPack) })
  .meta({ id: "RoleTemplateList" });

export const ConnectionStatus = z
  .enum(["connected", "missing", "not_available"])
  .meta({ id: "ConnectionStatus" });

export const RolePlanAccess = z
  .object({
    family: AccessFamily,
    /** The role's default (null: the role grants nothing). */
    role: Level4.nullable(),
    /** The overlay's or the vertical pack's ceiling, if any. */
    cap: AccessLevel.nullable(),
    /** What the DE holds. */
    level: Level4.nullable(),
    /** What applies now (Draft level during probation: at most A). */
    now: Level4.nullable(),
    actions: z.record(z.string(), z.enum(["R", "A", "D", "M", "never"])),
    source: AccessDefaultSource,
    connected: z.boolean(),
    widened: z.boolean(),
  })
  .meta({ id: "RolePlanAccess" });

export const RolePlanKpi = RoleKpiRow.extend({
  name: z.string(),
  source: z.enum(["role", "vertical", "overlay"]),
  /** The company's definition (null until the DE is set up from the role). */
  metric_def_id: Uuid.nullable(),
}).meta({ id: "RolePlanKpi" });

export const RolePlanProblem = z
  .object({ path: z.string(), code: z.string(), message: z.string() })
  .meta({ id: "RolePlanProblem" });

/** What a deploy from the role gives this DE: every layer merged (the wizard's prefill). */
export const RolePlan = z
  .object({
    template_id: Uuid,
    template_version_id: Uuid,
    template_key: z.string(),
    name: z.string(),
    vertical_overlay_id: Uuid.nullable(),
    overlay_ids: z.array(Uuid),
    skills: z.array(z.string()),
    blocked_skills: z.array(z.string()),
    access: z.array(RolePlanAccess),
    classes: z.record(z.string(), z.enum(["direct", "ask", "never"])),
    kpis: z.array(RolePlanKpi),
    cadence: z.array(RoleCadenceRow),
    model_tier: ModelTier,
    budget_minor: z.int().min(0),
    probation: RoleProbation,
    required_connections: z.array(RoleRequiredConnection.extend({ status: ConnectionStatus })),
    instructions: z.array(
      z.object({ layer: z.string(), label: z.string(), version: z.string(), text: z.string() }),
    ),
    problems: z.array(RolePlanProblem),
  })
  .meta({ id: "RolePlan" });

export const RolePlanQuery = z.object({
  de_membership_id: Uuid.optional().describe("The DE the plan is for (its own access, if any)"),
  vertical_overlay_id: Uuid.optional().describe("An applied vertical pack overlay of this role"),
});
export const RoleTemplateParams = z.object({ co: Uuid, template_id: Uuid });

const AccessMap = z
  .partialRecord(AccessFamily, AccessLevel)
  .meta({ maxProperties: AccessFamily.options.length });

export const ApplyRole = z
  .strictObject({
    template_version_id: Uuid.describe(
      "A published role template version (a DE already set up from the role may also move to a deprecated one: a rollback)",
    ),
    vertical_overlay_id: Uuid.nullable().optional(),
    /** The DE's own level per family (the access step); absent: the merged default. */
    access: AccessMap.optional(),
    /** Families whose widening above the role default the caller (owner or supervisor) confirms. */
    confirm_widen: z.array(AccessFamily).max(16).optional(),
    /** Required families not connected yet that the DE starts without. */
    connect_later: z.array(AccessFamily).max(16).optional(),
    /** The board the role's routines go on; absent: no routines are made. */
    routine_board_id: Uuid.nullable().optional(),
  })
  .meta({ id: "ApplyRole" });

export const DeProbation = z
  .object({
    started_at: Timestamp,
    until: Timestamp,
    tasks_required: z.int().min(0),
    tasks_done: z.int().min(0),
    ended_at: Timestamp.nullable(),
    /** Both conditions hold: the days have passed and the tasks are done. */
    met: z.boolean(),
    record: z.object({
      approved_as_is: z.int().min(0),
      edited: z.int().min(0),
      rejected: z.int().min(0),
    }),
  })
  .meta({ id: "DeProbation" });

export const DeAccessView = z
  .object({
    data: z.array(DeAccessDefault),
    /** The ceilings of the overlays over this DE. */
    caps: AccessCapsJson,
    probation: DeProbation.nullable(),
  })
  .meta({ id: "DeAccessView" });

export const DeAccessPut = z
  .strictObject({
    access: AccessMap,
    confirm_widen: z.array(AccessFamily).max(16).optional(),
  })
  .meta({ id: "DeAccessPut" });

const overlayFields = {
  name: text(120),
  context_text: z.string().max(8000).nullable().optional(),
  tone_text: z.string().max(2000).nullable().optional(),
  add_skill_keys: z.array(SkillKey).max(32).optional(),
  block_skill_keys: z.array(SkillKey).max(32).optional(),
  access_caps: AccessCapsJson.optional(),
  blocked_actions: z.array(ClassKey).max(32).optional(),
  kpi_overrides: boundedRecord(
    z.number().min(0).max(1_000_000_000).nullable(),
    32,
    MetricKey,
  ).optional(),
  model_tier: ModelTier.nullable().optional(),
  budget_minor: z.int().min(0).max(100_000_000).nullable().optional(),
};

export const TemplateOverlayCreate = z
  .strictObject({
    /** The role it applies to; null or absent: every role. */
    template_key: SkillKey.nullable().optional(),
    ...overlayFields,
  })
  .meta({ id: "TemplateOverlayCreate" });

export const TemplateOverlayPatch = z
  .strictObject({ ...overlayFields, name: text(120).optional() })
  .refine((b) => Object.keys(b).length > 0, { message: "nothing to change" })
  .meta({ id: "TemplateOverlayPatch" });

export const TemplateOverlayQuery = z.object({
  archived: z.enum(["true", "false"]).optional().describe("Include archived overlays"),
});

export const roleTemplateRoutes = {
  "roleTemplates.list": defineRoute({
    ...agents,
    id: "roleTemplates.list",
    method: "get",
    path: `${co}/role-templates`,
    tag: "templates",
    summary: "The role templates and vertical packs a DE can be deployed from",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: RoleTemplateList },
    errors: [],
    emits: [],
  }),
  "roleTemplates.plan": defineRoute({
    ...agents,
    id: "roleTemplates.plan",
    method: "get",
    path: `${co}/role-templates/{template_id}/plan`,
    tag: "templates",
    summary: "What a deploy from this role gives a DE: every layer merged, connections checked",
    access: "manager",
    params: RoleTemplateParams,
    query: RolePlanQuery,
    response: { status: 200, schema: RolePlan },
    errors: [],
    emits: [],
  }),
  "templateOverlays.list": defineRoute({
    ...agents,
    id: "templateOverlays.list",
    method: "get",
    path: `${co}/template-overlays`,
    tag: "templates",
    summary: "The company's overlays and applied vertical packs",
    access: "manager",
    params: CompanyParams,
    query: TemplateOverlayQuery,
    response: { status: 200, schema: z.object({ data: z.array(TemplateOverlay) }) },
    errors: [],
    emits: [],
  }),
  "templateOverlays.create": defineRoute({
    ...agents,
    id: "templateOverlays.create",
    method: "post",
    path: `${co}/template-overlays`,
    tag: "templates",
    summary: "Create the company's overlay (every role, or one role)",
    access: "manager",
    params: CompanyParams,
    body: TemplateOverlayCreate,
    idempotency: "required",
    response: { status: 201, schema: TemplateOverlay },
    errors: ["conflict"],
    emits: ["template_overlay.changed"],
  }),
  "templateOverlays.update": defineRoute({
    ...agents,
    id: "templateOverlays.update",
    method: "patch",
    path: `${co}/template-overlays/{id}`,
    tag: "templates",
    summary: "Edit an overlay (DEs pick it up at their next deploy)",
    access: "manager",
    params: CompanyItemParams,
    body: TemplateOverlayPatch,
    ifMatch: true,
    response: { status: 200, schema: TemplateOverlay },
    errors: ["conflict", "precondition_required"],
    emits: ["template_overlay.changed"],
  }),
  "templateOverlays.archive": defineRoute({
    ...agents,
    id: "templateOverlays.archive",
    method: "post",
    path: `${co}/template-overlays/{id}/archive`,
    tag: "templates",
    summary: "Archive an overlay",
    access: "manager",
    params: CompanyItemParams,
    ifMatch: true,
    response: { status: 200, schema: TemplateOverlay },
    errors: ["conflict", "precondition_required"],
    emits: ["template_overlay.changed"],
  }),
  "verticalPacks.apply": defineRoute({
    ...agents,
    id: "verticalPacks.apply",
    method: "post",
    path: `${co}/vertical-packs/{id}/apply`,
    tag: "templates",
    summary: "Apply a vertical pack: one overlay per role it covers, editable afterwards",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "required",
    response: { status: 201, schema: z.object({ data: z.array(TemplateOverlay) }) },
    errors: ["conflict"],
    emits: ["template_overlay.changed"],
  }),
  "digitalEmployees.applyRole": defineRoute({
    ...agents,
    id: "digitalEmployees.applyRole",
    method: "post",
    path: `${co}/digital-employees/{id}/role`,
    tag: "digital-employees",
    summary:
      "Set a DE up from a role: overlays, access, KPI definitions, seat policy, routines, probation",
    description:
      "Run before the deploy: the bundle reads the DE's layers. KPI targets are then set with `PUT …/job` over the returned `kpis[].metric_def_id`. Widening a family above the role default needs the caller to be an owner or the DE's supervisor and to list it in `confirm_widen`; nothing goes above an overlay's cap.",
    access: "manager",
    params: CompanyItemParams,
    body: ApplyRole,
    idempotency: "required",
    response: { status: 200, schema: RolePlan },
    errors: ["conflict", "connections_missing", "forbidden"],
    emits: [
      "digital_employee.role_applied",
      "de_access_default.changed",
      "metric_def.created",
      "agent_policy.changed",
      "responsibility.created",
      "recurring_work.created",
    ],
  }),
  "digitalEmployees.access": defineRoute({
    ...agents,
    id: "digitalEmployees.access",
    method: "get",
    path: `${co}/digital-employees/{id}/access`,
    tag: "digital-employees",
    summary: "The DE's access per family, the overlay caps and its probation",
    description: "A manager, or the person supervising the DE's seat (404 to anyone else).",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: DeAccessView },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.putAccess": defineRoute({
    ...agents,
    id: "digitalEmployees.putAccess",
    method: "put",
    path: `${co}/digital-employees/{id}/access`,
    tag: "digital-employees",
    summary:
      "Change the DE's access (a manager or its supervisor; tighten freely; widen with the owner's or supervisor's confirmation)",
    access: "member",
    params: CompanyItemParams,
    body: DeAccessPut,
    response: { status: 200, schema: DeAccessView },
    errors: ["conflict", "forbidden"],
    emits: ["de_access_default.changed"],
  }),
  "digitalEmployees.endProbation": defineRoute({
    ...agents,
    id: "digitalEmployees.endProbation",
    method: "post",
    path: `${co}/digital-employees/{id}/end-probation`,
    tag: "digital-employees",
    summary: "End the DE's probation (its supervisor or an owner, once the days and tasks are met)",
    description:
      "The person supervising the DE's seat, or an owner; another manager is 403, anyone else 404.",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: DeProbation },
    errors: ["conflict", "forbidden", "probation_not_met", "connections_missing"],
    emits: ["digital_employee.probation_ended"],
  }),
} as const;
